/*
 * The shop checkout (the Prints mockup's .flow-head, .co, .co-form, .tg-line,
 * .fs, .fld, .hint, .saved-card, .ship-opt, .co-sum, .sum-item, .line and its
 * phone rules), in theme tokens. In the telify layer.
 */
@layer telify {
  .head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 20px;
    flex-wrap: wrap;
    margin-bottom: 30px;
  }

  .title {
    font-family: var(--font-display);
    font-weight: 500;
    font-size: clamp(42px, 4.6vw, 64px);
    line-height: 1;
  }

  .back {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
    color: var(--text-secondary);
  }

  .co {
    display: grid;
    grid-template-columns: minmax(0, 1.25fr) minmax(0, 0.9fr);
    gap: 56px;
    align-items: start;
  }

  .form {
    display: grid;
    gap: 34px;
    min-width: 0;
  }

  .tg {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    flex-wrap: wrap;
    padding: 16px 20px;
    border: 1px solid rgba(var(--accent-rgb), 0.35);
    background: rgba(var(--accent-rgb), 0.06);
    border-radius: 6px;
  }

  .tg p {
    display: flex;
    align-items: center;
    gap: 12px;
    font-size: 15px;
    color: var(--text-secondary);
  }

  .tg svg {
    width: 22px;
    height: 22px;
    flex: none;
    color: var(--accent);
  }

  .tgIn {
    border-color: var(--border-soft);
    background: var(--tint);
  }

  .sm {
    height: 40px;
    padding: 0 20px;
    font-size: 14px;
  }

  .fs {
    border: 0;
    min-width: 0;
    display: grid;
    gap: 14px;
  }

  .fs legend {
    padding: 0;
    margin-bottom: 14px;
    font-family: var(--font-display);
    font-size: 26px;
    font-weight: 500;
  }

  .row3 {
    display: grid;
    gap: 14px;
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  .fld {
    display: grid;
    gap: 6px;
    min-width: 0;
  }

  .fld label {
    font-size: 13px;
    letter-spacing: 0.03em;
    color: var(--text-tertiary);
  }

  .fld input,
  .fld select {
    width: 100%;
    height: 48px;
    padding: 0 14px;
    border-radius: 5px;
    border: 1px solid var(--field-line);
    background: var(--tint);
    color: var(--text-primary);
    font: inherit;
    font-size: 15.5px;
  }

  .fld select {
    appearance: none;
    -webkit-appearance: none;
    background-image:
      linear-gradient(45deg, transparent 50%, var(--text-tertiary) 50%),
      linear-gradient(135deg, var(--text-tertiary) 50%, transparent 50%);
    background-position:
      calc(100% - 20px) 21px,
      calc(100% - 15px) 21px;
    background-size: 5px 5px;
    background-repeat: no-repeat;
  }

  .fld select option {
    background: var(--bg-page);
    color: var(--text-primary);
  }

  .fld input:focus,
  .fld select:focus {
    outline: none;
    border-color: var(--accent);
    box-shadow: 0 0 0 3px rgba(var(--accent-rgb), 0.12);
  }

  .fld input::placeholder {
    color: var(--text-muted);
  }

  .err {
    font-size: 13px;
    color: var(--danger);
  }

  .bad input,
  .bad select {
    border-color: var(--danger);
  }

  .hint {
    margin-top: -4px;
    font-size: 13px;
    color: var(--text-tertiary);
  }

  .saved {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    padding: 16px 18px;
    border-radius: 6px;
    border: 1px solid var(--field-line);
    background: var(--tint);
  }

  .saved p {
    font-size: 15px;
    line-height: 1.5;
  }

  .sub {
    color: var(--text-tertiary);
    font-size: 13.5px;
  }

  .textlink {
    flex: none;
    font-size: 15px;
    color: var(--accent);
    background: none;
    border: 0;
    border-bottom: 1px solid rgba(var(--accent-rgb), 0.4);
    padding: 0 0 3px;
    cursor: pointer;
  }

  .ship {
    display: flex;
    justify-content: space-between;
    gap: 16px;
    padding: 16px 18px;
    border-radius: 6px;
    border: 1px solid var(--field-line);
  }

  .shipOn {
    border-color: var(--accent);
    background: rgba(var(--accent-rgb), 0.06);
  }

  .ship b {
    font-weight: 500;
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
  }

  .refused,
  .alert {
    font-size: 14.5px;
    color: var(--danger);
  }

  .tick {
    display: flex;
    gap: 10px;
    align-items: flex-start;
    font-size: 14.5px;
    line-height: 1.45;
    color: var(--text-secondary);
    cursor: pointer;
  }

  .tick input {
    flex: none;
    width: 18px;
    height: 18px;
    margin: 1px 0 0;
    accent-color: var(--accent);
  }

  .wide {
    width: 100%;
  }

  .wide:disabled {
    background: transparent;
    color: var(--text-muted);
    border-color: var(--border-soft);
    cursor: default;
  }

  .sum {
    position: sticky;
    top: calc(var(--head-h) + 24px);
    padding: 26px 26px 24px;
    border: var(--line);
    background: var(--bg-card);
  }

  .sumBody {
    display: grid;
    gap: 14px;
  }

  .sumBody h2 {
    font-family: var(--font-display);
    font-weight: 500;
    font-size: 26px;
  }

  .toggle {
    display: none;
  }

  .items {
    display: grid;
    gap: 14px;
    padding-bottom: 16px;
    border-bottom: var(--line);
  }

  .item {
    display: grid;
    grid-template-columns: 56px minmax(0, 1fr) auto;
    gap: 14px;
    align-items: center;
    font-variant-numeric: tabular-nums;
  }

  .thumb {
    position: relative;
    width: 56px;
    height: 68px;
    background: var(--tint);
  }

  .q {
    position: absolute;
    top: -7px;
    right: -7px;
    min-width: 20px;
    height: 20px;
    border-radius: 999px;
    background: var(--accent);
    color: var(--text-on-accent);
    font-size: 11.5px;
    line-height: 20px;
    text-align: center;
  }

  .itemName {
    font-family: var(--font-display);
    font-size: 18px;
    line-height: 1.15;
  }

  .line {
    display: flex;
    justify-content: space-between;
    gap: 16px;
    font-size: 15px;
    font-variant-numeric: tabular-nums;
  }

  .big {
    font-size: 18px;
    padding-top: 12px;
    border-top: var(--line);
  }

  @media (max-width: 1140px) {
    .co {
      gap: 36px;
    }
  }

  @media (max-width: 860px) {
    .co {
      grid-template-columns: 1fr;
      gap: 26px;
    }

    .sum {
      position: static;
      order: -1;
      padding: 0;
      border: 0;
      background: none;
    }

    .sumBody h2 {
      display: none;
    }

    .toggle {
      display: flex;
      justify-content: space-between;
      align-items: center;
      width: 100%;
      height: 52px;
      padding: 0 16px;
      border: var(--line);
      background: var(--bg-card);
      color: var(--accent);
      font-size: 15px;
      cursor: pointer;
      font-variant-numeric: tabular-nums;
    }

    .sumBody {
      display: none;
      padding: 16px;
      border: var(--line);
      border-top: 0;
      background: var(--bg-card);
      gap: 12px;
    }

    .open .sumBody {
      display: grid;
    }

    .row3 {
      grid-template-columns: 1fr;
    }
  }
}
